/**
 * An example conversation with an AI assistant that has Babia connected:
 * logging both twins at once, a question about the log, and the overview's
 * "did we miss anything?" check with its fix offered, not made. Illustrative
 * text, not any one assistant's interface; the receipts mirror what the MCP
 * tools report.
 */
import { cn } from "../../lib/utils.ts";

type Turn =
  | { from: "parent"; text: string }
  | { from: "assistant"; text: string }
  | { from: "babia"; emoji: string; text: string };

const TURNS: Turn[] = [
  { from: "parent", text: "Ada had 4 oz at 2, Bea had 3." },
  { from: "babia", emoji: "🍼", text: "Logged · Ada 4 oz, Bea 3 oz of Similac at 2:00 am" },
  { from: "assistant", text: "Both bottles are on the log at 2:00 am." },
  { from: "parent", text: "When did Bea last have a dirty diaper?" },
  {
    from: "assistant",
    text: "Yesterday at 6:40 pm, about 7½ hours ago. Her last change was wet, at 1:52 am.",
  },
  { from: "parent", text: "Did we miss anything?" },
  {
    from: "assistant",
    text: "One thing: Ada's nap from 11:20 pm is still running, but her bottle was logged at 2:00 am. Should I end the nap at 1:55?",
  },
  { from: "parent", text: "Yes, thanks." },
  {
    from: "babia",
    emoji: "🌅",
    text: "Ada is awake after a 2 hr 35 min nap (11:20 pm to 1:55 am)",
  },
];

export function Conversation({ className }: { className?: string }) {
  return (
    <figure className={cn("min-w-0", className)}>
      <div className="border border-divider border-t-[3px] border-t-ink bg-paper">
        <div className="flex items-center gap-2 border-b border-rule px-4 py-2.5">
          <span className="kicker opacity-70">Your AI assistant</span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] opacity-80">
            <span className="size-[7px] rounded-full bg-accent" aria-hidden="true" />
            Babia connected
          </span>
        </div>
        <ol className="flex flex-col gap-2.5 px-4 py-4 text-[15px] leading-snug">
          {TURNS.map((turn, i) => (
            <li key={i} className="flex">
              {turn.from === "parent" ? (
                <p className="ml-auto max-w-[85%] rounded-md bg-surface px-3.5 py-2">
                  <span className="sr-only">You: </span>
                  {turn.text}
                </p>
              ) : turn.from === "assistant" ? (
                <p className="max-w-[92%] py-0.5">
                  <span className="sr-only">Assistant: </span>
                  {turn.text}
                </p>
              ) : (
                <p className="flex items-start gap-2 border-l-[3px] border-accent bg-accent-tint px-3 py-1.5 text-[13px] text-accent-deep">
                  <span aria-hidden="true">{turn.emoji}</span>
                  <span>
                    <span className="heading">Babia</span> · {turn.text}
                  </span>
                </p>
              )}
            </li>
          ))}
        </ol>
      </div>
      <figcaption className="mt-2 text-[13px] italic opacity-65">
        An example conversation. Babia works inside the assistant you already use.
      </figcaption>
    </figure>
  );
}
